import { BUDGET_SECTIONS } from './constants';
import { parseCurrency } from './currency';
import type {
  BudgetFieldValues,
  BudgetResult,
  BudgetSection,
  BudgetSectionKey,
  BudgetSegment,
  BudgetSegmentKey,
  BudgetStatus,
} from './types';

/** 50/30/20 share of income for each category. */
const THRESHOLDS: Record<BudgetSectionKey, number> = {
  essentials: 0.5,
  wants: 0.3,
  savings: 0.2,
};

interface SegmentAmount {
  key: BudgetSegmentKey;
  amount: number;
}

/** Avoid floating-point rounding errors in money comparisons. */
const toCents = (value: number): number => Math.round(value * 100);

const sumSection = (section: BudgetSection, values: BudgetFieldValues): number =>
  section.fields.reduce((total, field) => total + parseCurrency(values[field.label] ?? ''), 0);

const resolveStatus = (differenceInCents: number): BudgetStatus => {
  if (differenceInCents === 0) return 'balanced';

  return differenceInCents > 0 ? 'under' : 'over';
};

/** Income is validated as positive before calling, keeping percentage divisions safe. */
export const calculateBudget = (values: BudgetFieldValues, income: number): BudgetResult => {
  const sectionAmounts: SegmentAmount[] = BUDGET_SECTIONS.map(section => ({
    key: section.key,
    amount: sumSection(section, values),
  }));

  const allocated = sectionAmounts.reduce((total, { amount }) => total + amount, 0);
  const differenceInCents = toCents(income) - toCents(allocated);
  const status = resolveStatus(differenceInCents);
  const delta = Math.abs(differenceInCents) / 100;

  const segmentAmounts: SegmentAmount[] =
    status === 'under' ? [{ key: 'extra', amount: delta }, ...sectionAmounts] : sectionAmounts;

  // Normalize by the total so over-budget stacks still fit the fixed-height track.
  const segmentTotal = segmentAmounts.reduce((total, { amount }) => total + amount, 0);

  const segments: BudgetSegment[] = segmentAmounts.map(({ key, amount }) => {
    const percentOfIncome = Math.round((amount / income) * 100);

    return {
      key,
      amount,
      percentOfIncome,
      // Compare the rounded display value so a pill reading "50%" is never over.
      isOver: key !== 'extra' && percentOfIncome > THRESHOLDS[key] * 100,
      basis: (amount / segmentTotal) * 100,
    };
  });

  return {
    income,
    allocated,
    delta,
    status,
    reference: {
      essentials: income * THRESHOLDS.essentials,
      wants: income * THRESHOLDS.wants,
      savings: income * THRESHOLDS.savings,
    },
    segments,
  };
};
